import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  User, Phone, Mail, MapPin, CreditCard, Calendar, History,
  Loader2, Edit, Hotel
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function GuestDetailsTab({ tabId, data }) {
  const { closeTab, openTab } = useTabs();
  const { guestId, guest: initialGuest } = data || {};
  
  const [guest, setGuest] = useState(initialGuest || null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(!initialGuest);

  useEffect(() => {
    loadData();
  }, [guestId]);

  const loadData = async () => {
    try {
      const [guestRes, bookingsRes] = await Promise.all([
        initialGuest ? Promise.resolve({ data: initialGuest }) : api.get(`/guests/${guestId}`),
        api.get(`/bookings?guest_id=${guestId || initialGuest?.id}`)
      ]);
      
      setGuest(guestRes.data);
      setBookings(bookingsRes.data || []);
    } catch (error) {
      toast.error('Failed to load guest details');
    } finally {
      setLoading(false);
    }
  };

  const openBookingDetails = (booking) => {
    openTab({
      type: TAB_TYPES.BOOKING_DETAILS,
      id: booking.id,
      title: `Booking #${booking.id?.substring(0, 8).toUpperCase()}`,
      data: { booking }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        <span className="ml-2 text-gray-600">Loading guest details...</span>
      </div>
    );
  }

  if (!guest) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Guest not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <User className="h-6 w-6 text-cyan-600" />
            {guest.name || `${guest.first_name} ${guest.last_name}`}
          </h1>
          <p className="text-gray-500 mt-1">
            Guest since {new Date(guest.created_at).toLocaleDateString()}
          </p>
        </div>
        <Button 
          variant="outline"
          onClick={() => openTab({
            type: TAB_TYPES.GUEST_EDIT,
            id: `edit-${guest.id}`,
            title: `Edit: ${guest.name || 'Guest'}`,
            data: { guestId: guest.id, guest, onRefresh: loadData }
          })}
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Guest
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-3 gap-6 overflow-hidden">
        {/* Left Column - Guest Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="py-3 px-4 bg-cyan-50 border-b">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-cyan-700">
                <User className="h-4 w-4" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium">{guest.phone || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium">{guest.email || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="font-medium">
                    {typeof guest.address === 'object' && guest.address 
                      ? [guest.address.address_line1, guest.address.address_line2, guest.address.city, guest.address.state, guest.address.pin_code, guest.address.country].filter(Boolean).join(', ')
                      : (guest.address || 'N/A')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3 px-4 bg-gray-50 border-b">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                ID Document
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">ID Type</span>
                <span className="font-medium">{guest.id_type_name || guest.id_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">ID Number</span>
                <span className="font-medium">{guest.id_number || 'N/A'}</span>
              </div>
              {guest.id_proof_image && (
                <div className="mt-2">
                  <img 
                    src={guest.id_proof_image} 
                    alt="ID Proof" 
                    className="w-full rounded-lg border"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Booking History */}
        <div className="col-span-2">
          <Card className="h-full flex flex-col">
            <CardHeader className="py-3 px-4 bg-gray-50 border-b flex-shrink-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <History className="h-4 w-4" />
                Booking History ({bookings.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="divide-y">
                  {bookings.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No booking history found
                    </div>
                  ) : (
                    bookings.map(booking => (
                      <div 
                        key={booking.id} 
                        className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => openBookingDetails(booking)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                              <Hotel className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">
                                Room {booking.room_number || booking.room_type_name || 'N/A'}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(booking.check_in_date || booking.check_in).toLocaleDateString()} - {new Date(booking.check_out_date || booking.check_out).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={`
                              ${booking.status === 'checked_out' ? 'bg-gray-100 text-gray-700' :
                                booking.status === 'checked_in' ? 'bg-green-100 text-green-700' :
                                booking.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'}
                            `}>
                              {booking.status?.replace('_', ' ')}
                            </Badge>
                            <p className="text-sm font-semibold text-gray-800 mt-1">
                              ₹{booking.total_amount?.toFixed(0) || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
        <Button variant="outline" onClick={() => closeTab(tabId)}>
          Close
        </Button>
      </div>
    </div>
  );
}
